// Site Features System - Toggleable features for generated sites
// Updated MVP feature set: static + Google Places powered

export type FeatureCategory = 'essential' | 'engagement' | 'commerce' | 'media';

export interface Feature {
  id: string;
  name: string;
  description: string;
  category: FeatureCategory;
  icon: string;
  defaultEnabled: boolean;
}

export interface FeatureCategoryInfo {
  id: FeatureCategory;
  name: string;
  description: string;
  icon: string;
}

export const FEATURE_CATEGORIES: FeatureCategoryInfo[] = [
  {
    id: 'essential',
    name: 'Essential',
    description: 'Core features every business website needs',
    icon: 'star',
  },
  {
    id: 'engagement',
    name: 'Engagement',
    description: 'Connect with visitors and build trust',
    icon: 'users',
  },
  {
    id: 'commerce',
    name: 'Commerce',
    description: 'Drive sales and inquiries',
    icon: 'shopping-cart',
  },
  {
    id: 'media',
    name: 'Media',
    description: 'Showcase your work visually',
    icon: 'image',
  },
];

export const FEATURES: Feature[] = [
  // Essential (6 features)
  {
    id: 'contact-form',
    name: 'Contact Form',
    description: 'Let customers reach out directly through your website',
    category: 'essential',
    icon: 'mail',
    defaultEnabled: true,
  },
  {
    id: 'phone-display',
    name: 'Phone Display',
    description: 'Prominently display your phone number with click-to-call',
    category: 'essential',
    icon: 'phone',
    defaultEnabled: true,
  },
  {
    id: 'address-map',
    name: 'Address & Map',
    description: 'Show your location with a map link',
    category: 'essential',
    icon: 'map-pin',
    defaultEnabled: true,
  },
  {
    id: 'business-hours',
    name: 'Business Hours',
    description: 'Display your operating hours clearly',
    category: 'essential',
    icon: 'clock',
    defaultEnabled: true,
  },
  {
    id: 'about-section',
    name: 'About Section',
    description: 'Tell your story and build credibility',
    category: 'essential',
    icon: 'info',
    defaultEnabled: true,
  },
  {
    id: 'services-list',
    name: 'Services List',
    description: 'Showcase all services you offer',
    category: 'essential',
    icon: 'list',
    defaultEnabled: true,
  },

  // Engagement (4 features)
  {
    id: 'testimonials',
    name: 'Testimonials',
    description: 'Display customer reviews and success stories',
    category: 'engagement',
    icon: 'message-square',
    defaultEnabled: true,
  },
  {
    id: 'google-reviews',
    name: 'Google Reviews',
    description: 'Show Google Business reviews (snapshot at generation time)',
    category: 'engagement',
    icon: 'star',
    defaultEnabled: true,
  },
  {
    id: 'social-links',
    name: 'Social Links',
    description: 'Connect visitors to your social media profiles',
    category: 'engagement',
    icon: 'share-2',
    defaultEnabled: true,
  },
  {
    id: 'faq',
    name: 'FAQ Section',
    description: 'Answer common questions upfront',
    category: 'engagement',
    icon: 'help-circle',
    defaultEnabled: false,
  },

  // Commerce (3 features)
  {
    id: 'price-list',
    name: 'Price List',
    description: 'Display your services with pricing',
    category: 'commerce',
    icon: 'dollar-sign',
    defaultEnabled: false,
  },
  {
    id: 'quote-request',
    name: 'Quote Request',
    description: 'Let customers request custom quotes',
    category: 'commerce',
    icon: 'file-text',
    defaultEnabled: false,
  },
  {
    id: 'promotions',
    name: 'Promotions Banner',
    description: 'Highlight special offers and discounts',
    category: 'commerce',
    icon: 'tag',
    defaultEnabled: false,
  },

  // Media (3 features)
  {
    id: 'photo-gallery',
    name: 'Photo Gallery',
    description: 'Showcase your work with Google Places photos',
    category: 'media',
    icon: 'image',
    defaultEnabled: true,
  },
  {
    id: 'video-section',
    name: 'Video Section',
    description: 'Embed a YouTube or Vimeo video',
    category: 'media',
    icon: 'video',
    defaultEnabled: false,
  },
  {
    id: 'portfolio',
    name: 'Portfolio',
    description: 'Display your best projects in a grid',
    category: 'media',
    icon: 'folder',
    defaultEnabled: false,
  },
];

// Helper functions
export function getFeatureById(id: string): Feature | undefined {
  return FEATURES.find((f) => f.id === id);
}

export function getFeaturesByCategory(category: FeatureCategory): Feature[] {
  return FEATURES.filter((f) => f.category === category);
}

export function getDefaultEnabledFeatures(): string[] {
  return FEATURES.filter((f) => f.defaultEnabled).map((f) => f.id);
}

export function getCategoryInfo(category: FeatureCategory): FeatureCategoryInfo | undefined {
  return FEATURE_CATEGORIES.find((c) => c.id === category);
}
